import { forwardRef } from "react";
import { Copy, Instagram } from "lucide-react";
import { format } from "date-fns";
import { fmtIDR } from "@/lib/constants";
import { BANK_DETAILS, INSTAGRAM_HANDLE, paymentHistoryRows } from "@/lib/invoicePdf";

const LOGO = "/brand/heels-and-tea-logo.jpg";

const Row = ({ label, value, strong, accent }) => (
  <div className="flex items-baseline justify-between gap-6 py-[5px]">
    <span className={`text-[12px] tracking-wide ${strong ? "font-semibold text-neutral-900" : "text-neutral-500"}`}>{label}</span>
    <span className={`text-[13px] tabular-nums ${strong ? "font-bold text-neutral-900" : "text-neutral-700"} ${accent ? "text-[#b76e79]" : ""}`}>{value}</span>
  </div>
);

/** Customer-facing invoice. Renders ONLY the saved invoice version — never recalculated. */
const InvoiceDocument = forwardRef(function InvoiceDocument({ invoice, onCopyAccount }, ref) {
  const paid = invoice.paid_verified;
  const isPaid = invoice.balance_due <= 0;
  const payments = invoice.payments || [];

  return (
    <div
      ref={ref}
      data-testid="invoice-document"
      className="mx-auto w-full max-w-[820px] bg-white px-8 py-10 text-neutral-900 sm:px-14 sm:py-16"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <div className="flex flex-col items-center text-center">
        <img src={LOGO} alt="Heels & Tea" crossOrigin="anonymous" className="h-28 w-28 object-contain sm:h-36 sm:w-36" data-testid="invoice-logo" />
        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.42em] text-[#b76e79]">Final Invoice</p>
      </div>

      <div className="mt-10 h-px w-full bg-gradient-to-r from-transparent via-[#f0d4d8] to-transparent" />

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Customer</p>
          <p className="mt-2 font-heading text-lg font-bold" data-testid="invoice-doc-customer">{invoice.customer_name}</p>
          {invoice.customer_phone && <p className="text-[12px] text-neutral-600">{invoice.customer_phone}</p>}
          {invoice.customer_instagram && <p className="text-[12px] text-neutral-600">@{invoice.customer_instagram}</p>}
        </div>
        <div className="sm:text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Invoice No.</p>
          <p className="mt-2 text-[13px] font-bold tabular-nums" data-testid="invoice-doc-number">
            {invoice.invoice_number || `HT-${invoice.id.slice(0, 8).toUpperCase()}`}
          </p>
          <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Invoice date</p>
          <p className="text-[13px] text-neutral-700" data-testid="invoice-doc-date">{format(new Date(invoice.created_at), "d MMMM yyyy")}</p>
        </div>
      </div>

      <table className="mt-10 w-full border-collapse" data-testid="invoice-doc-lines">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Product</th>
            <th className="pb-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Qty</th>
            <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Unit Price</th>
            <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((l, i) => (
            <tr key={i} className="border-b border-neutral-100">
              <td className="py-3 pr-3 text-[13px] font-medium">
                {l.product_name}
                {l.variant ? <span className="text-neutral-500"> · {l.variant}</span> : null}
              </td>
              <td className="py-3 text-center text-[13px] tabular-nums">{l.quantity}</td>
              <td className="py-3 text-right text-[13px] tabular-nums text-neutral-600">{l.selling_price != null ? fmtIDR(l.selling_price) : "—"}</td>
              <td className="py-3 text-right text-[13px] font-semibold tabular-nums">{l.selling_price != null ? fmtIDR(l.line_total) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-8 flex justify-end">
        <div className="w-full sm:w-[340px]">
          <Row label="SUBTOTAL" value={fmtIDR(invoice.subtotal)} />
          <Row label="ONGKOS KIRIM" value={fmtIDR(invoice.shipping_charge)} />
          {invoice.insurance_amount > 0 && <Row label="Insurance" value={fmtIDR(invoice.insurance_amount)} />}
          {invoice.adjustment !== 0 && <Row label="Adjustment" value={fmtIDR(invoice.adjustment)} />}
          <div className="my-2 h-px w-full bg-neutral-200" />
          <Row label="TOTAL" value={fmtIDR(invoice.total)} strong />
          <Row label="SUDAH DIBAYAR" value={fmtIDR(paid)} />
          {invoice.credit_used > 0 && <Row label="CUSTOMER CREDIT" value={fmtIDR(invoice.credit_used)} />}
          <div className="my-2 h-px w-full bg-neutral-200" />
          <div className="flex items-baseline justify-between gap-6 py-1">
            <span className="text-[12px] font-semibold tracking-wide text-neutral-900">SISA TAGIHAN</span>
            <span className="font-heading text-xl font-extrabold tabular-nums" data-testid="invoice-doc-balance">{fmtIDR(Math.max(invoice.balance_due, 0))}</span>
          </div>
          <div className="mt-4 flex sm:justify-end">
            <span
              data-testid="invoice-doc-status"
              className={`rounded-full border px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] ${
                isPaid ? "border-[#e8c9ce] bg-[#fdf3f4] text-[#b76e79]" : "border-neutral-300 bg-neutral-50 text-neutral-700"
              }`}
            >
              {isPaid ? "Paid" : "Balance Due"}
            </span>
          </div>
        </div>
      </div>

      {payments.length > 0 && (
        <div className="mt-10" data-testid="invoice-doc-payments">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Riwayat DP</p>
          <div className="mt-2 space-y-1">
            {paymentHistoryRows(payments).map((p) => (
              <div key={p.id} className="flex justify-between border-b border-neutral-100 py-2 text-[12px]">
                <span className="text-neutral-600">
                  <b className="text-neutral-800">{p.label}</b> · {p.method} · {p.paid_at}
                </span>
                <span className="font-semibold tabular-nums">{fmtIDR(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 rounded-lg border border-[#c3d4ef] bg-[#f2f6fd] px-6 py-5 text-center" data-testid="invoice-bank-details">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#2d5ca8]">Payment Details</p>
        <p className="mt-2 text-[12px] uppercase tracking-[0.08em] text-[#4a6fa5]">
          Bank {BANK_DETAILS.bank} · A/N {BANK_DETAILS.account_holder}
        </p>
        <p className="mt-1 flex items-center justify-center gap-2 font-heading text-xl font-extrabold tracking-wide tabular-nums text-[#1f4b91]">
          <span data-testid="invoice-bank-account" className="select-all">{BANK_DETAILS.account_number}</span>
          {onCopyAccount && (
            <button
              type="button"
              data-testid="invoice-copy-account"
              aria-label="Copy account number"
              onClick={onCopyAccount}
              className="text-[#4a6fa5] transition-colors hover:text-[#1f4b91]"
            >
              <Copy className="h-4 w-4" strokeWidth={2.2} />
            </button>
          )}
        </p>
      </div>

      {invoice.notes && <p className="mt-8 text-[12px] italic text-neutral-600" data-testid="invoice-doc-notes">{invoice.notes}</p>}

      <div className="mt-14 border-t border-neutral-200 pt-6 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#b76e79]">Thank you for shopping with us</p>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-[12px] font-medium text-neutral-500" data-testid="invoice-instagram">
          <Instagram className="h-3.5 w-3.5" strokeWidth={2} />
          {INSTAGRAM_HANDLE}
        </p>
      </div>
    </div>
  );
});

export default InvoiceDocument;
